import React, { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import classNames from "classnames";
import configs from "../../utils/configs";
import { CreateRoomButton } from "./CreateRoomButton";
import { PWAButton } from "./PWAButton";
import { useFavoriteRooms } from "./useFavoriteRooms";
import { usePublicRooms } from "./usePublicRooms";
import styles from "./HomePage.scss";
import { AuthContext } from "../auth/AuthContext";
import { createAndRedirectToNewHub, hubUrl, isLocalClient } from "../../utils/phoenix-utils";
import { MediaGrid } from "../room/MediaGrid";
import { MediaTile } from "../room/MediaTiles";
import { PageContainer } from "../layout/PageContainer";
import { scaledThumbnailUrlFor } from "../../utils/media-url-utils";
import { Column } from "../layout/Column";
import { Container } from "../layout/Container";
import { SocialBar } from "../home/SocialBar";
import { SignInButton } from "./SignInButton";
import { isHmc } from "../../utils/isHmc";
import maskEmail from "../../utils/mask-email";
import immersiveMeetupPoster from "../../assets/images/i3/i3-immersive-meetup-poster.png";

export function HomePage() {
  const auth = useContext(AuthContext);
  const intl = useIntl();

  const { results: favoriteRooms } = useFavoriteRooms();
  const { results: publicRooms } = usePublicRooms();

  const sortedFavoriteRooms = Array.from(favoriteRooms).sort((a, b) => b.member_count - a.member_count);
  const sortedPublicRooms = Array.from(publicRooms).sort((a, b) => b.member_count - a.member_count);
  const hasPublicRooms = sortedPublicRooms.length > 0;
  const isPreview = process.env.IS_UI_PREVIEW || window.location.port === "4173" || window.location.port === "4174";
  const roomForCurrentClient = room =>
    room.type === "room" && isLocalClient() ? { ...room, url: hubUrl(room.id, {}).href } : room;
  const featuredRoom = hasPublicRooms ? roomForCurrentClient(sortedPublicRooms[0]) : null;
  const featuredRoomUrl = featuredRoom?.url || (isPreview ? "/?demo=device&entry=featured" : null);
  const wrapInBold = chunk => <b>{chunk}</b>;
  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    // Support legacy sign in urls.
    if (qs.has("sign_in")) {
      const redirectUrl = new URL("/signin", window.location);
      redirectUrl.search = location.search;
      window.location = redirectUrl;
    } else if (qs.has("auth_topic")) {
      const redirectUrl = new URL("/verify", window.location);
      redirectUrl.search = location.search;
      window.location = redirectUrl;
    }

    if (qs.has("new")) {
      qs.delete("new");
      if (!process.env.IS_UI_PREVIEW && window.location.port !== "4173") {
        createAndRedirectToNewHub(null, null, true, qs).catch(error => {
          console.error("Room creation failed", error);
        });
      }
    }
  }, []);

  const canCreateRooms = isPreview || !configs.feature("disable_room_creation") || auth.isAdmin;
  const email = auth.email;
  return (
    <PageContainer className={styles.homePage}>
      <Container className={styles.heroContainer}>
        <div className={styles.hero}>
          {auth.isSignedIn ? (
            <div className={styles.signInContainer}>
              <span>
                <FormattedMessage
                  id="header.signed-in-as"
                  defaultMessage="Signed in as {email}"
                  values={{ email: maskEmail(email) }}
                />
              </span>
              <a href="#" onClick={auth.signOut} className={styles.mobileSignOut}>
                <FormattedMessage id="header.sign-out" defaultMessage="Sign Out" />
              </a>
            </div>
          ) : (
            <SignInButton mobile />
          )}
          <div className={styles.appInfo}>
            <div className={styles.eyebrow}>
              <span aria-hidden="true" />
              <FormattedMessage id="home-page.i3-eyebrow" defaultMessage="Immersive collaboration" />
            </div>
            <h1 className={styles.appDescription}>{configs.translation("app-description")}</h1>
            <p className={styles.heroCopy}>
              <FormattedMessage
                id="home-page.i3-hero-copy"
                defaultMessage="Connect from your desktop or mobile, or join in VR. Everyone meets in the same shared 3D space."
              />
            </p>
            <div className={styles.heroActions}>
              {canCreateRooms && <CreateRoomButton />}
              {featuredRoomUrl && (
                <a className={styles.secondaryAction} href={featuredRoomUrl}>
                  {isPreview ? (
                    <FormattedMessage id="home-page.join-featured-event" defaultMessage="Join featured event" />
                  ) : (
                    <FormattedMessage id="home-page.join-event" defaultMessage="Join live event" />
                  )}
                </a>
              )}
              {hasPublicRooms && (
                <a className={styles.textAction} href="#public-rooms">
                  <FormattedMessage id="home-page.browse-spaces" defaultMessage="Browse all spaces" />
                </a>
              )}
              <PWAButton />
            </div>
            <div className={styles.experienceList}>
              <span>
                <FormattedMessage id="home-page.desktop" defaultMessage="Desktop" />
              </span>
              <span>
                <FormattedMessage id="home-page.mobile" defaultMessage="Mobile" />
              </span>
              <span>
                <FormattedMessage id="home-page.vr-headset" defaultMessage="VR headset" />
              </span>
            </div>
          </div>
          <div className={styles.heroImageContainer}>
            <div className={styles.heroGlow} aria-hidden="true" />
            <div className={styles.mediaFrame}>
              <img
                alt={intl.formatMessage(
                  {
                    id: "home-page.hero-image-alt",
                    defaultMessage: "Screenshot of {appName}"
                  },
                  { appName: configs.translation("app-name") }
                )}
                src={immersiveMeetupPoster}
              />
              <div className={styles.posterCopy}>
                <span>
                  <FormattedMessage id="home-page.featured-event" defaultMessage="Featured event" />
                </span>
                <strong>
                  <FormattedMessage id="home-page.temporary-event-name" defaultMessage="i3 Immersive Meetup" />
                </strong>
                <small>
                  <FormattedMessage id="home-page.event-detail" defaultMessage="Meet inside a shared 3D world" />
                </small>
              </div>
              <div className={styles.liveCard}>
                <span className={styles.liveIndicator} aria-hidden="true" />
                <div>
                  <strong>
                    <FormattedMessage id="home-page.live-event" defaultMessage="Live event" />
                  </strong>
                  <small>
                    <FormattedMessage id="home-page.live-event-detail" defaultMessage="Ready to join" />
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <Container className={styles.preJoinContainer} aria-labelledby="pre-join-title">
        <div className={styles.preJoinHeading}>
          <span>
            <FormattedMessage id="home-page.before-you-join-label" defaultMessage="Before you join" />
          </span>
          <h2 id="pre-join-title">
            <FormattedMessage id="home-page.before-you-join-title" defaultMessage="Enter the event with confidence" />
          </h2>
          <p>
            <FormattedMessage
              id="home-page.before-you-join-copy"
              defaultMessage="We will guide you through your device, audio and avatar before you enter the 3D space."
            />
          </p>
        </div>
        <ol className={styles.preJoinSteps}>
          <li>
            <span aria-hidden="true">1</span>
            <div>
              <strong>
                <FormattedMessage id="home-page.choose-device-title" defaultMessage="Choose your device" />
              </strong>
              <p>
                <FormattedMessage
                  id="home-page.choose-device-copy"
                  defaultMessage="Join from desktop or mobile, pair a VR headset, or watch without entering."
                />
              </p>
            </div>
          </li>
          <li>
            <span aria-hidden="true">2</span>
            <div>
              <strong>
                <FormattedMessage id="home-page.check-audio-title" defaultMessage="Check your audio" />
              </strong>
              <p>
                <FormattedMessage
                  id="home-page.check-audio-copy"
                  defaultMessage="Select your microphone and speakers, then test them before joining."
                />
              </p>
            </div>
          </li>
          <li>
            <span aria-hidden="true">3</span>
            <div>
              <strong>
                <FormattedMessage id="home-page.choose-avatar-title" defaultMessage="Choose how you appear" />
              </strong>
              <p>
                <FormattedMessage
                  id="home-page.choose-avatar-copy"
                  defaultMessage="Set your display name and avatar, then enter the shared 3D space."
                />
              </p>
            </div>
          </li>
        </ol>
      </Container>
      {configs.feature("show_feature_panels") && (
        <Container className={classNames(styles.features, styles.colLg, styles.centerLg)}>
          <Column padding gap="xl" className={styles.card}>
            <img src={configs.image("landing_rooms_thumb")} />
            <h3>
              <FormattedMessage id="home-page.rooms-title" defaultMessage="Instantly create rooms" />
            </h3>
            <p>
              <FormattedMessage
                id="home-page.rooms-blurb"
                defaultMessage="Share virtual spaces with your friends, co-workers, and communities. When you create a room with Hubs, you’ll have a private virtual meeting space that you can instantly share <b>- no downloads or VR headset necessary.</b>"
                values={{ b: wrapInBold }}
              />
            </p>
          </Column>
          <Column padding gap="xl" className={styles.card}>
            <img src={configs.image("landing_communicate_thumb")} />
            <h3>
              <FormattedMessage id="home-page.communicate-title" defaultMessage="Communicate and Collaborate" />
            </h3>
            <p>
              <FormattedMessage
                id="home-page.communicate-blurb"
                defaultMessage="Choose an avatar to represent you, put on your headphones, and jump right in. Hubs makes it easy to stay connected with voice and text chat to other people in your private room."
              />
            </p>
          </Column>
          <Column padding gap="xl" className={styles.card}>
            <img src={configs.image("landing_media_thumb")} />
            <h3>
              <FormattedMessage id="home-page.media-title" defaultMessage="An easier way to share media" />
            </h3>
            <p>
              <FormattedMessage
                id="home-page.media-blurb"
                defaultMessage="Share content with others in your room by dragging and dropping photos, videos, PDF files, links, and 3D models into your space."
              />
            </p>
          </Column>
        </Container>
      )}
      {hasPublicRooms && (
        <Container id="public-rooms" className={styles.roomsContainer}>
          <h3 className={styles.roomsHeading}>
            <FormattedMessage id="home-page.public--rooms" defaultMessage="Public Rooms" />
          </h3>
          <Column grow padding className={styles.rooms}>
            <MediaGrid center>
              {sortedPublicRooms.map(room => {
                const roomEntry = roomForCurrentClient(room);
                return (
                  <MediaTile
                    key={room.id}
                    entry={roomEntry}
                    processThumbnailUrl={(entry, width, height) =>
                      scaledThumbnailUrlFor(entry.images.preview.url, width, height)
                    }
                  />
                );
              })}
            </MediaGrid>
          </Column>
        </Container>
      )}
      {sortedFavoriteRooms.length > 0 && (
        <Container className={styles.roomsContainer}>
          <h3 className={styles.roomsHeading}>
            <FormattedMessage id="home-page.favorite-rooms" defaultMessage="Favorite Rooms" />
          </h3>
          <Column grow padding className={styles.rooms}>
            <MediaGrid center>
              {sortedFavoriteRooms.map(room => {
                const roomEntry = roomForCurrentClient(room);
                return (
                  <MediaTile
                    key={room.id}
                    entry={roomEntry}
                    processThumbnailUrl={(entry, width, height) =>
                      scaledThumbnailUrlFor(entry.images.preview.url, width, height)
                    }
                  />
                );
              })}
            </MediaGrid>
          </Column>
        </Container>
      )}
      {isHmc() ? (
        <Column center>
          <SocialBar />
        </Column>
      ) : null}
    </PageContainer>
  );
}
